/**
 * DbPopupField - 弹窗表单字段组件
 *
 * 基于 DbTitlePair + DbPopup + DbRadio + DbInput 组合而成的复合组件。
 * 点击触发行弹出 Popup，支持两种交互模式：
 *   - input 模式：文本输入（如订单备注）
 *   - radio 模式：单选选项（如选择退货原因）
 *
 * 特性：
 * - 基于 TitlePair 展示当前选中值 / 输入内容
 * - 点击后弹出底部或居中 Popup
 * - 支持受控和非受控两种使用方式
 * - 支持确认 / 取消操作
 * - 支持 ref 命令式调用（open / close / getValue / reset）
 * - 高度可定制的样式和配置
 */

import React, {
    useState,
    useMemo,
    useCallback,
    useRef,
    useEffect,
    forwardRef,
    useImperativeHandle,
} from 'react';
import {
    View,
    Text,
    TextInput,
    TouchableOpacity,
} from 'react-native';
import DbTitlePair from '../DbTitlePair';
import DbPopup from '../DbPopup';
import DbRadio from '../DbRadio';
import DbIcon from '../DbIcon';
import type {
    DbPopupFieldProps,
    DbPopupFieldRef,
} from './DbPopupField.types';
import { createStyles, POPUP_FIELD_DEFAULTS } from './DbPopupField.styles';

// ==================== 右箭头组件（使用 DbIcon）====================

const ArrowRight: React.FC<{ size?: number; color?: string }> = ({
    size = 16,
    color = POPUP_FIELD_DEFAULTS.arrowColor,
}) => (
    <DbIcon name="chevron-forward" size={size} color={color} />
);

// ==================== 主组件 ====================

function DbPopupFieldInner<T = string>(
    props: DbPopupFieldProps<T>,
    ref: React.Ref<DbPopupFieldRef>,
) {
    const {
        // 核心属性
        mode,
        value: controlledValue,
        defaultValue,
        onChange,
        onConfirm,
        onCancel,

        // 触发行配置
        title,
        placeholder,
        displayText,
        subtitleContent,
        titleLayout = 'space-between',
        required = false,
        disabled = false,
        showArrow = true,
        arrowColor,
        leftExtra,
        showDivider = false,

        // 标题样式
        titleFontSize = POPUP_FIELD_DEFAULTS.titleFontSize,
        titleColor = POPUP_FIELD_DEFAULTS.titleColor,
        titleFontWeight = POPUP_FIELD_DEFAULTS.titleFontWeight,
        valueFontSize = POPUP_FIELD_DEFAULTS.valueFontSize,
        valueColor = POPUP_FIELD_DEFAULTS.valueColor,
        placeholderColor = POPUP_FIELD_DEFAULTS.placeholderColor,

        // 模式配置
        inputConfig = {},
        radioConfig,
        customConfig,
        popupConfig = {},

        // 样式
        style,
        titlePairStyle,
        contentPadding,
    } = props;

    const styles = useMemo(() => createStyles(contentPadding), [contentPadding]);

    // ==================== 状态管理 ====================

    const isControlled = controlledValue !== undefined;
    const [internalValue, setInternalValue] = useState<T | undefined>(defaultValue);
    const currentValue = isControlled ? controlledValue : internalValue;

    // 弹窗内的临时值（确认前不影响外部值）
    const [draftValue, setDraftValue] = useState<T | undefined>(currentValue);
    const [visible, setVisible] = useState(false);
    const inputRef = useRef<TextInput>(null);

    // 同步受控值变化到 draft
    useEffect(() => {
        if (!visible) {
            setDraftValue(currentValue);
        }
    }, [currentValue, visible]);

    // ==================== Ref 命令式 API ====================

    useImperativeHandle(ref, () => ({
        open: () => {
            if (!disabled) {
                handleOpen();
            }
        },
        close: () => handleClose(),
        getValue: () => currentValue,
        reset: () => {
            const resetVal = defaultValue;
            if (!isControlled) {
                setInternalValue(resetVal);
            }
            setDraftValue(resetVal);
            onChange?.(resetVal as T);
        },
    }));

    // ==================== 弹窗控制 ====================

    const handleOpen = useCallback(() => {
        setDraftValue(currentValue);
        setVisible(true);
    }, [currentValue]);

    const handleClose = useCallback(() => {
        setVisible(false);
    }, []);

    const handleConfirm = useCallback(() => {
        if (!isControlled) {
            setInternalValue(draftValue);
        }
        onChange?.(draftValue as T);
        onConfirm?.(draftValue as T);
        setVisible(false);
    }, [draftValue, isControlled, onChange, onConfirm]);

    const handleCancel = useCallback(() => {
        setDraftValue(currentValue);
        onCancel?.();
        setVisible(false);
    }, [currentValue, onCancel]);

    // ==================== 输入模式处理 ====================

    const handleInputChange = useCallback((text: string) => {
        setDraftValue(text as unknown as T);
    }, []);

    // ==================== 单选模式处理 ====================

    const handleRadioChange = useCallback((val: T) => {
        setDraftValue(val);
        // 单选模式下选中后直接确认
        if (!popupConfig.showActions) {
            if (!isControlled) {
                setInternalValue(val);
            }
            onChange?.(val);
            onConfirm?.(val);
            setVisible(false);
        }
    }, [isControlled, onChange, onConfirm, popupConfig.showActions]);

    // ==================== 显示文本计算 ====================

    const computeDisplayText = useMemo(() => {
        // 自定义 displayText
        if (displayText !== undefined) {
            if (typeof displayText === 'function') {
                return displayText(currentValue);
            }
            return displayText;
        }

        // 没有值时显示占位符
        if (currentValue === undefined || currentValue === null || currentValue === '') {
            return undefined; // 返回 undefined 表示显示 placeholder
        }

        // radio 模式：查找选项的 label
        if (mode === 'radio' && radioConfig?.options) {
            const option = radioConfig.options.find(
                (opt) => opt.value === currentValue,
            );
            return option?.label ?? String(currentValue);
        }

        // input 模式：直接显示文本
        return String(currentValue);
    }, [currentValue, displayText, mode, radioConfig?.options]);

    const hasValue = computeDisplayText !== undefined;
    const subtitleText = hasValue
        ? computeDisplayText
        : (placeholder ?? (mode === 'input' ? '请输入' : '请选择'));

    // ==================== 弹窗配置 ====================

    const {
        title: popupTitle,
        position: popupPosition = POPUP_FIELD_DEFAULTS.popupPosition,
        height: popupHeight,
        maxHeight: popupMaxHeight,
        closable = false,
        closeIconPosition = 'top-right',
        confirmText = POPUP_FIELD_DEFAULTS.confirmText,
        cancelText = POPUP_FIELD_DEFAULTS.cancelText,
        // 输入模式默认显示底部操作栏，custom 模式根据 customConfig.showActions 决定
        showActions = mode === 'input' ? true : (customConfig?.showActions ?? false),
        round = true,
        borderRadius = POPUP_FIELD_DEFAULTS.popupBorderRadius,
        popupStyle,
        contentStyle,
    } = popupConfig;

    // ==================== 输入框配置 ====================

    const {
        type: inputType = 'textarea',
        placeholder: inputPlaceholder = POPUP_FIELD_DEFAULTS.inputPlaceholder,
        maxLength = POPUP_FIELD_DEFAULTS.inputMaxLength,
        rows = POPUP_FIELD_DEFAULTS.inputRows,
        autoGrow = false,
        showCount = true,
        inputStyle: customInputStyle,
        inputContainerStyle,
    } = inputConfig;

    // ==================== 渲染触发行 ====================

    const renderTrigger = () => {
        // 如果有自定义 subtitleContent，直接使用它
        if (subtitleContent !== undefined) {
            return (
                <DbTitlePair
                    title={title}
                    subtitle={{ content: subtitleContent }}
                    layout={titleLayout}
                    titleFontSize={titleFontSize}
                    titleColor={titleColor}
                    titleFontWeight={titleFontWeight}
                    required={required}
                    requiredPosition="start"
                    leftExtra={leftExtra}
                    showDivider={showDivider}
                    style={[styles.triggerRow, titlePairStyle]}
                    onPress={disabled ? undefined : handleOpen}
                />
            );
        }

        // 默认行为：文本 + 箭头
        const rightExtraContent = showArrow ? (
            <ArrowRight
                size={16}
                color={arrowColor ?? POPUP_FIELD_DEFAULTS.arrowColor}
            />
        ) : undefined;

        return (
            <DbTitlePair
                title={title}
                subtitle={{
                    text: subtitleText,
                    color: hasValue ? valueColor : placeholderColor,
                    fontSize: valueFontSize,
                    fontWeight: '400',
                    numberOfLines: 1,
                }}
                layout={titleLayout}
                titleFontSize={titleFontSize}
                titleColor={titleColor}
                titleFontWeight={titleFontWeight}
                required={required}
                requiredPosition="start"
                leftExtra={leftExtra}
                rightExtra={rightExtraContent}
                showDivider={showDivider}
                style={[styles.triggerRow, titlePairStyle]}
                onPress={disabled ? undefined : handleOpen}
            />
        );
    };

    // ==================== 渲染弹窗头部 ====================

    const renderPopupHeader = () => {
        const headerTitle = popupTitle ?? title;

        return (
            <View style={styles.popupHeader}>
                <TouchableOpacity
                    style={styles.popupHeaderBtn}
                    onPress={handleCancel}
                    activeOpacity={0.7}
                >
                    <Text style={styles.popupHeaderCancelText}>{cancelText}</Text>
                </TouchableOpacity>

                <Text style={styles.popupHeaderTitle} numberOfLines={1}>
                    {headerTitle}
                </Text>

                <TouchableOpacity
                    style={styles.popupHeaderBtn}
                    onPress={handleConfirm}
                    activeOpacity={0.7}
                >
                    <Text style={styles.popupHeaderConfirmText}>{confirmText}</Text>
                </TouchableOpacity>
            </View>
        );
    };

    // ==================== 渲染输入内容 ====================

    const renderInputContent = () => {
        const draftStr = (draftValue as unknown as string) ?? '';
        const isMultiline = inputType === 'textarea';
        const inputHeight = isMultiline ? Math.max(rows * 22, 88) : 44;

        return (
            <View style={[styles.popupContent, contentStyle]}>
                <View style={[styles.inputWrapper, inputContainerStyle]}>
                    <TextInput
                        ref={inputRef}
                        value={draftStr}
                        onChangeText={handleInputChange}
                        placeholder={inputPlaceholder}
                        placeholderTextColor={POPUP_FIELD_DEFAULTS.placeholderColor}
                        maxLength={maxLength}
                        multiline={isMultiline}
                        numberOfLines={isMultiline ? rows : 1}
                        style={[
                            styles.inputField,
                            {
                                height: autoGrow ? undefined : inputHeight,
                                minHeight: isMultiline ? inputHeight : undefined,
                            },
                            customInputStyle,
                        ]}
                        textAlignVertical={isMultiline ? 'top' : 'center'}
                        autoFocus={true}
                        returnKeyType={isMultiline ? 'default' : 'done'}
                        blurOnSubmit={!isMultiline}
                    />
                    {showCount && maxLength && (
                        <View style={styles.inputCounter}>
                            <Text style={styles.inputCountText}>
                                <Text
                                    style={[
                                        styles.inputCountText,
                                        draftStr.length > 0 && styles.inputCountActive,
                                    ]}
                                >
                                    {draftStr.length}
                                </Text>
                                /{maxLength}
                            </Text>
                        </View>
                    )}
                </View>
            </View>
        );
    };

    // ==================== 渲染单选内容 ====================

    const renderRadioContent = () => {
        if (!radioConfig?.options) {
            return null;
        }

        return (
            <View style={[styles.popupContent, contentStyle]}>
                <DbRadio
                    options={radioConfig.options}
                    value={draftValue}
                    onChange={handleRadioChange}
                    layout={radioConfig.layout ?? 'right'}
                    size={radioConfig.size ?? 'medium'}
                    activeColor={radioConfig.activeColor ?? POPUP_FIELD_DEFAULTS.primaryColor}
                    inactiveColor={radioConfig.inactiveColor}
                    radioConfig={radioConfig.radioConfig}
                    spacing={radioConfig.spacing}
                    itemStyle={radioConfig.itemStyle}
                    labelStyle={radioConfig.labelStyle}
                />
            </View>
        );
    };

    // ==================== 渲染自定义内容 ====================

    const renderCustomContent = () => {
        if (!customConfig?.customContent) {
            return null;
        }

        const { customContent, contentStyle: customContentStyle } = customConfig;

        // 渲染函数形式
        if (typeof customContent === 'function') {
            return (
                <View style={[styles.popupContent, customContentStyle]}>
                    {customContent({
                        onConfirm: (value) => {
                            if (!isControlled) {
                                setInternalValue(value);
                            }
                            onChange?.(value);
                            onConfirm?.(value);
                            setVisible(false);
                        },
                        onCancel: () => {
                            setDraftValue(currentValue);
                            onCancel?.();
                            setVisible(false);
                        },
                        onClose: () => {
                            setVisible(false);
                        },
                    })}
                </View>
            );
        }

        // ReactNode 形式
        return (
            <View style={[styles.popupContent, customContentStyle]}>
                {customContent}
            </View>
        );
    };

    // ==================== 渲染底部操作栏 ====================

    const renderActionBar = () => {
        if (!showActions) return null;

        return (
            <View style={styles.actionBar}>
                <TouchableOpacity
                    style={[styles.actionBtn, styles.actionBtnCancel]}
                    onPress={handleCancel}
                    activeOpacity={0.7}
                >
                    <Text style={styles.actionBtnCancelText}>{cancelText}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                    style={[styles.actionBtn, styles.actionBtnConfirm]}
                    onPress={handleConfirm}
                    activeOpacity={0.7}
                >
                    <Text style={styles.actionBtnConfirmText}>{confirmText}</Text>
                </TouchableOpacity>
            </View>
        );
    };

    // ==================== 渲染弹窗 ====================

    const renderPopup = () => {
        // 计算弹窗高度
        const computedHeight = popupHeight ??
            (mode === 'input'
                ? undefined   // input 模式高度自适应
                : undefined); // radio 模式高度自适应

        // 底部弹窗使用 header 导航（取消 / 确定），居中弹窗使用底部 actionBar
        const isBottom = popupPosition === 'bottom';
        const useHeader = isBottom && mode !== 'custom'; // custom 模式可以选择是否使用 header
        const useActionBar = !isBottom && showActions;

        // 根据模式渲染对应的内容
        const renderContent = () => {
            switch (mode) {
                case 'input':
                    return renderInputContent();
                case 'custom':
                    return renderCustomContent();
                case 'radio':
                default:
                    return renderRadioContent();
            }
        };

        return (
            <DbPopup
                visible={visible}
                position={popupPosition}
                height={computedHeight}
                maxHeight={popupMaxHeight}
                round={round}
                borderRadius={borderRadius}
                closable={closable && !useHeader}
                closeIconPosition={closeIconPosition}
                onClose={handleCancel}
                title={useHeader ? undefined : (popupTitle ?? title)}
                safeAreaInsetBottom={isBottom}
                style={popupStyle}
                overlay
                closeOnClickOverlay
            >
                {useHeader && renderPopupHeader()}
                {renderContent()}
                {useActionBar && renderActionBar()}
            </DbPopup>
        );
    };

    // ==================== 主渲染 ====================

    return (
        <View style={[styles.container, style]}>
            {renderTrigger()}
            {renderPopup()}
        </View>
    );
}

// ==================== 使用 forwardRef 包装泛型组件 ====================

export const DbPopupField = forwardRef(DbPopupFieldInner) as <T = string>(
    props: DbPopupFieldProps<T> & { ref?: React.Ref<DbPopupFieldRef> },
) => React.ReactElement | null;

// 设置 displayName
(DbPopupField as any).displayName = 'DbPopupField';

export default DbPopupField;
export type {
    DbPopupFieldProps,
    DbPopupFieldRef,
    PopupFieldMode,
    PopupFieldInputConfig,
    PopupFieldRadioConfig,
    PopupFieldPopupConfig,
} from './DbPopupField.types';
